'use client';

import type { KeyboardEvent } from 'react';
import { useRef } from 'react';

export interface OptionPickerOption {
  text: string;
  value: string;
}

export interface OptionPickerProps {
  options: OptionPickerOption[];
  ariaLabelledBy: string;
  selected: string;
  onSelect: (value: string) => void;
}

export function OptionPicker({ options, ariaLabelledBy, selected, onSelect }: OptionPickerProps) {
  const radioGroupRef = useRef<HTMLDivElement>(null);

  if (!options.length) return null;

  const handleKeyDown = (event: KeyboardEvent, index: number) => {
    if (event.key === ' ') {
      event.preventDefault();
      onSelect(options[index].value);
    } else if (event.key === 'Enter') {
      onSelect(options[index].value);
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      const next = (index + 1) % options.length;
      onSelect(options[next].value);
      radioGroupRef.current?.querySelectorAll<HTMLElement>('[role="radio"]')[next]?.focus();
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault();
      const previous = (index - 1 + options.length) % options.length;
      onSelect(options[previous].value);
      radioGroupRef.current?.querySelectorAll<HTMLElement>('[role="radio"]')[previous]?.focus();
    }
  };

  return (
    <div
      ref={radioGroupRef}
      aria-labelledby={ariaLabelledBy}
      className="mb-3 grid grid-cols-2 gap-1.5"
      role="radiogroup"
    >
      {options.map((option, index) => {
        const isSelected = selected === option.value;

        return (
          <div
            key={option.value}
            aria-checked={isSelected}
            className={`flex cursor-pointer items-center gap-2 rounded-[8px] bg-card transition-colors hover:border-secondary-foreground focus-visible:focus-ring ${
              isSelected
                ? 'border-2 border-secondary-foreground px-[9px] py-2'
                : 'border-[1.5px] border-black/15 px-2.5 py-[9px]'
            }`}
            onClick={() => onSelect(option.value)}
            onKeyDown={event => handleKeyDown(event, index)}
            role="radio"
            tabIndex={isSelected ? 0 : -1}
          >
            <span
              aria-hidden="true"
              className={`flex size-4 shrink-0 items-center justify-center rounded-full border-2 ${
                isSelected ? 'border-secondary-foreground' : 'border-black/30'
              }`}
            >
              {isSelected ? <span className="size-2 rounded-full bg-secondary-foreground" /> : null}
            </span>
            <span className="font-sans text-xs leading-normal font-medium text-foreground">
              {option.text}
            </span>
          </div>
        );
      })}
    </div>
  );
}
